import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  fetchNotifications,
  getNotificationCount,
  markNotificationsAsRead,
  markAllNotificationsAsRead,
  subscribeToNotifications,
} from '@/features/notifications/api/notificationsApi'
import { useAuth } from '@/features/auth/AuthContext'

export default function NotificationCenter({ isTransparent = false }) {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications().then(res => res.data),
    enabled: !!user,
  })

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notification-count'],
    queryFn: getNotificationCount,
    enabled: !!user,
    refetchInterval: 30000,
  })

  useEffect(() => {
    if (!user) return

    const unsubscribe = subscribeToNotifications(user.id, (newNotification) => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notification-count'])

      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/favicon.ico',
        })
      }
    })

    return unsubscribe
  }, [user, queryClient])

  const markAsReadMutation = useMutation({
    mutationFn: markNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notification-count'])
    },
  })

  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notification-count'])
    },
  })

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate([notification.id])
    }
    setIsOpen(false)
  }

  const handleMarkAllRead = () => {
    markAllAsReadMutation.mutate()
  }

  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }

  if (!user) return null

  const unreadNotifications = notifications.filter(n => !n.read)

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 focus:outline-none transition-colors ${
          isTransparent
            ? 'text-white hover:text-white/80'
            : 'text-secondary-600 hover:text-secondary-900'
        }`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-2 w-96 bg-white rounded-lg shadow-lg border border-secondary-200 max-h-[600px] flex flex-col">
            <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-secondary-900">Notifications</h3>
              {unreadNotifications.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <svg
                    className="w-12 h-12 mx-auto mb-4 text-secondary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="text-secondary-600">No notifications yet</p>
                  {('Notification' in window && Notification.permission === 'default') && (
                    <button
                      onClick={requestNotificationPermission}
                      className="mt-4 text-sm text-primary-600 hover:text-primary-700"
                    >
                      Enable browser notifications
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-secondary-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-secondary-50 transition-colors ${
                        !notification.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      {notification.link ? (
                        <Link
                          to={notification.link}
                          onClick={() => handleNotificationClick(notification)}
                          className="block"
                        >
                          <NotificationContent notification={notification} />
                        </Link>
                      ) : (
                        <div onClick={() => handleNotificationClick(notification)}>
                          <NotificationContent notification={notification} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function NotificationContent({ notification }) {
  const priorityColors = {
    low: 'text-secondary-600',
    normal: 'text-blue-600',
    high: 'text-orange-600',
    urgent: 'text-red-600',
  }

  const timeAgo = getTimeAgo(notification.created_at)

  return (
    <>
      <div className="flex items-start gap-3">
        <div className={`mt-1 ${priorityColors[notification.priority] || priorityColors.normal}`}>
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <circle cx="10" cy="10" r="8" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary-900">{notification.title}</p>
          <p className="text-sm text-secondary-600 mt-1">{notification.message}</p>
          <p className="text-xs text-secondary-500 mt-2">{timeAgo}</p>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 bg-primary-600 rounded-full mt-2" />
        )}
      </div>
    </>
  )
}

function getTimeAgo(timestamp) {
  const now = new Date()
  const past = new Date(timestamp)
  const diffMs = now.getTime() - past.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return past.toLocaleDateString()
}
